/**
 * ProviderCompare
 *
 * EXPERIMENTAL, admin-only "Relationship" tab on the provider detail page: the
 * relationship between two providers, now and over time. Built entirely from
 * the historical data tree on data.doctivityhealth.com (the same per-NPI
 * files the Trends tab reads) — no REST endpoints.
 *
 * ORDER MATTERS. Shared-patient edges are directional: "first" saw the patient
 * before "second". The page's provider starts as first; the swap button flips
 * the pair. Each provider keeps its own color through a flip, and a direction
 * is drawn in the color of whoever saw the patient first.
 *
 * Both directions come from the PAGE provider's files (shared-after = page
 * provider first, shared-before = the other provider first), filtered to the
 * other provider's NPI, so flipping never refetches.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import Chart from 'react-apexcharts';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Button,
  Card,
  CardContent,
  FormControl,
  GridLegacy as Grid,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  FindProviderDialog,
  LoadingView,
  TitleTooltip,
} from 'components';
import {
  StoreUtil,
} from 'doctivity-shared/utils';
import { ColorUtil } from 'utils';
import { downloadRelationshipPDF } from 'utils/pdf-export';
import { HoverLegendLineChart } from './HoverLegendLineChart';
import {
  loadTrendingData,
  trendingKey,
  TrendingDataType,
  TrendingEntityType,
} from 'store/actions/trendingActions';
import { listClaimsServicelines } from 'store/actions/claimsActions';
import CLAIMS_CFG from '../../config/claims-date.js';

const ALL = 'all';
const MIX_COUNT = 12;
const TREND_COUNT = 6;
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
// LexisNexis small-cell suppression arrives as a null count (<11); plotted as
// 5, the same stand-in TrendsSharedAffiliationCard and the DB analytics use
const SUPPRESSED_VALUE = 5;
const PAGE_COLOR = ColorUtil.COLORS[0];
const OTHER_COLOR = ColorUtil.COLORS[2];

// the picked provider per page provider, so the pair survives tab switches
const lastOtherByProviderId = new Map();

const styles = (theme) => ({
  pair: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  slot: {
    minWidth: 220,
    paddingLeft: theme.spacing(1.5),
    borderLeft: '4px solid',
  },
  slotLabel: {
    fontSize: 12,
    color: '#8E8E8E',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  slotName: {
    fontSize: 18,
    fontWeight: 500,
  },
  spacer: {
    flex: 1,
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  filter: {
    minWidth: 240,
  },
  hint: {
    fontSize: 13,
    color: '#666666',
    marginTop: theme.spacing(2),
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.primary.main,
  },
  stat: {
    fontSize: 32,
    fontWeight: 300,
    lineHeight: 1.2,
  },
  statLabel: {
    fontSize: 13,
    color: '#666666',
  },
  statDelta: {
    fontSize: 13,
  },
  note: {
    fontSize: 12,
    color: '#8E8E8E',
    marginTop: theme.spacing(1),
  },
});

function trendingQuarters() {
  const { trendingStartQuarter: start, trendingEndQuarter: end } = CLAIMS_CFG;
  const quarters = [];
  let { year, quarter } = start;
  while (year < end.year || (year === end.year && quarter <= end.quarter)) {
    quarters.push({ year, quarter });
    quarter++;
    if (quarter > 4) {
      quarter = 1;
      year++;
    }
  }
  return quarters;
}

// A measurement quarter is NOT three months of data: it holds the trailing 12
// months ending in that quarter, so every label spells out the full range
// (2026 Q1 -> "Apr 2025 – Mar 2026").
function periodOf(q) {
  const endMonth = q.quarter * 3;
  const startYear = endMonth === 12 ? q.year : q.year - 1;
  const end = `${MONTHS[endMonth - 1]} ${q.year}`;
  return { end, range: `${MONTHS[endMonth % 12]} ${startYear} – ${end}` };
}

function providerName(provider) {
  return [provider?.first_name, provider?.last_name].filter(Boolean).join(' ');
}

function isPresent(entry) {
  return !!(entry && entry.loaded && entry.data && entry.data.length > 0);
}

function isSettled(entry) {
  return !!(entry && (entry.loaded || entry.error));
}

// One quarter of a provider's shared-* file -> shared patients with one other
// provider, by serviceline slug. null = no file that quarter (a data gap);
// an empty Map = the file exists but has no edge to that provider (a real 0).
function sharedBySlug(entry, otherNpi) {
  if (!isPresent(entry)) return null;
  const bySlug = new Map();
  entry.data.forEach((row) => {
    if (String(row.provider_npi) !== String(otherNpi)) return;
    const patients =
      row.shared_patients == null ? SUPPRESSED_VALUE : row.shared_patients;
    bySlug.set(row.serviceline, (bySlug.get(row.serviceline) || 0) + patients);
  });
  return bySlug;
}

// One quarter of patients.jsonl -> projected patients by serviceline slug,
// summed across care settings (same roll-up as ServicelineTrendsChart)
function patientsBySlug(entry) {
  if (!isPresent(entry)) return null;
  const bySlug = new Map();
  entry.data.forEach((row) => {
    bySlug.set(
      row.serviceline,
      (bySlug.get(row.serviceline) || 0) + (row.total_patients || 0),
    );
  });
  return bySlug;
}

function lastPresentIndex(...lists) {
  for (let i = lists[0].length - 1; i >= 0; i--) {
    if (lists.some((list) => list[i])) return i;
  }
  return -1;
}

function useQuarterEntries(entityKey, quarters, dataType) {
  const dispatch = useDispatch();
  const keys = useMemo(
    () =>
      quarters.map((q) =>
        trendingKey(
          TrendingEntityType.PROVIDER,
          entityKey,
          q.year,
          q.quarter,
          dataType,
        ),
      ),
    [entityKey, quarters, dataType],
  );
  useEffect(() => {
    if (!entityKey) return;
    quarters.forEach((q) => {
      dispatch(
        loadTrendingData(
          TrendingEntityType.PROVIDER,
          entityKey,
          q.year,
          q.quarter,
          dataType,
        ),
      );
    });
  }, [dispatch, entityKey, quarters, dataType]);
  return useSelector(
    (state) => keys.map((k) => state.trending.entries[k]),
    shallowEqual,
  );
}

function StatCard({
  classes, label, detail, period, previousPeriod, color, value, previous,
}) {
  let delta = null;
  if (value != null && previous != null) {
    const diff = value - previous;
    let deltaColor = '#8E8E8E';
    if (diff > 0) deltaColor = ColorUtil.GreenColor;
    if (diff < 0) deltaColor = ColorUtil.RedColor;
    delta = (
      <Typography className={classes.statDelta} style={{ color: deltaColor }}>
        {`${diff > 0 ? '+' : ''}${diff.toLocaleString()} vs. ${previousPeriod}`}
      </Typography>
    );
  }
  return (
    <Card style={{ height: '100%' }}>
      <CardContent>
        <Typography className={classes.stat} style={{ color }}>
          {value == null ? '—' : value.toLocaleString()}
        </Typography>
        <Typography className={classes.statLabel}>{label}</Typography>
        {detail && (
          <Typography className={classes.statLabel}>{detail}</Typography>
        )}
        <Typography className={classes.statLabel}>{period}</Typography>
        {delta}
      </CardContent>
    </Card>
  );
}

StatCard.propTypes = {
  classes: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  detail: PropTypes.string,
  period: PropTypes.string,
  previousPeriod: PropTypes.string,
  color: PropTypes.string,
  value: PropTypes.number,
  previous: PropTypes.number,
};

function ProviderCompare({ classes, provider }) {
  const dispatch = useDispatch();
  const quarters = useMemo(() => trendingQuarters(), []);
  const periods = useMemo(() => quarters.map(periodOf), [quarters]);
  const categories = useMemo(() => periods.map((p) => p.end), [periods]);

  const [other, setOther] = useState(
    () => lastOtherByProviderId.get(provider?.id) || null,
  );
  const [flipped, setFlipped] = useState(false);
  const [finding, setFinding] = useState(false);
  const [servicelineSel, setServicelineSel] = useState(ALL);
  const [quarterSel, setQuarterSel] = useState(null); // null = latest with data

  useEffect(() => {
    setOther(lastOtherByProviderId.get(provider?.id) || null);
    setFlipped(false);
  }, [provider?.id]);

  const clientId = useSelector((state) => state.app.selectedClient);
  const userId = useSelector((state) => state.user?.id);
  const servicelines = useSelector(
    (state) => state.claims && state.claims.servicelines_grouped,
    shallowEqual,
  );
  const servicelinesList = useSelector(
    (state) => StoreUtil.get(state.claims, 'servicelines_list'),
    shallowEqual,
  );
  useEffect(() => {
    if (!servicelines && StoreUtil.needsLoadNoCache(servicelinesList)) {
      dispatch(listClaimsServicelines());
    }
  }, [dispatch, servicelines, servicelinesList]);

  const pageKey = provider?.npi || null;
  const otherKey = other?.npi || null;

  const afterEntries = useQuarterEntries(
    pageKey,
    quarters,
    TrendingDataType.SHARED_AFTER,
  );
  const beforeEntries = useQuarterEntries(
    pageKey,
    quarters,
    TrendingDataType.SHARED_BEFORE,
  );
  const pagePatientEntries = useQuarterEntries(
    pageKey,
    quarters,
    TrendingDataType.PROJECTED_PATIENTS,
  );
  const otherPatientEntries = useQuarterEntries(
    otherKey,
    quarters,
    TrendingDataType.PROJECTED_PATIENTS,
  );

  // top-level standard servicelines (custom client servicelines have no slug)
  const parents = useMemo(
    () =>
      (servicelines || [])
        .filter((s) => !s.client_id && s.slug)
        .slice()
        .sort((a, b) => (a.name < b.name ? -1 : 1)),
    [servicelines],
  );
  const selectedParent =
    servicelineSel === ALL
      ? null
      : parents.find((p) => p.id === servicelineSel);
  // the bars in the by-serviceline cards: parents, or the parent's children
  const breakdown = useMemo(() => {
    if (!selectedParent) return parents;
    const children = (selectedParent.children || []).filter(
      (c) => !c.client_id && c.slug,
    );
    return children.length > 0 ? children : [selectedParent];
  }, [selectedParent, parents]);

  const data = useMemo(() => {
    if (!otherKey) return null;
    // page provider first / other provider first
    const pageFirst = afterEntries.map((e) => sharedBySlug(e, otherKey));
    const otherFirst = beforeEntries.map((e) => sharedBySlug(e, otherKey));
    const pagePatients = pagePatientEntries.map(patientsBySlug);
    const otherPatients = otherPatientEntries.map(patientsBySlug);
    return { pageFirst, otherFirst, pagePatients, otherPatients };
  }, [
    otherKey,
    afterEntries,
    beforeEntries,
    pagePatientEntries,
    otherPatientEntries,
  ]);

  const onProviderSelected = (selected) => {
    setFinding(false);
    if (!selected || selected.id === provider.id) return;
    lastOtherByProviderId.set(provider.id, selected);
    setOther(selected);
    setFlipped(false);
    setQuarterSel(null);
  };

  const findDialog = finding && (
    <FindProviderDialog
      clientId={clientId}
      userId={userId}
      open
      onSelect={onProviderSelected}
    />
  );

  if (!pageKey) {
    return (
      <Card>
        <CardContent>
          <Typography className={classes.empty}>
            This provider has no NPI, so there is no historical data to compare.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!other) {
    return (
      <Card>
        <CardContent>
          <TitleTooltip
            title='Provider Relationship'
            tooltip='Experimental. The relationship between two providers: their shared patients and their own volumes over time, from the historical data.'
          />
          <Typography className={classes.empty}>
            {`Pick a provider to compare with ${providerName(provider)}.`}
            <br />
            <br />
            <Button
              variant='contained'
              color='primary'
              onClick={() => setFinding(true)}
            >
              <Icon style={{ marginRight: 8 }}>compare_arrows</Icon>
              Choose provider
            </Button>
          </Typography>
          {findDialog}
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent>
          <Typography className={classes.empty}>
            {`${providerName(other)} has no NPI, so there is no historical data to compare.`}
            <br />
            <br />
            <Button color='primary' onClick={() => setFinding(true)}>
              Choose another provider
            </Button>
          </Typography>
          {findDialog}
        </CardContent>
      </Card>
    );
  }

  const page = { provider, color: PAGE_COLOR, name: providerName(provider) };
  const picked = { provider: other, color: OTHER_COLOR, name: providerName(other) };
  const first = flipped ? picked : page;
  const second = flipped ? page : picked;

  // Shared patients follow the ONE direction picked at the top (first saw the
  // patient before second); the swap button shows the other direction.
  const firstToSecond = flipped ? data.otherFirst : data.pageFirst;
  const firstPatients = flipped ? data.otherPatients : data.pagePatients;
  const secondPatients = flipped ? data.pagePatients : data.otherPatients;
  const forwardLabel = `${first.name}, then ${second.name}`;

  const sharedSettled =
    afterEntries.every(isSettled) && beforeEntries.every(isSettled);
  const patientsSettled =
    pagePatientEntries.every(isSettled) && otherPatientEntries.every(isSettled);
  const isLoading = !servicelines || !sharedSettled || !patientsSettled;

  const latestShared = lastPresentIndex(firstToSecond);
  const latestPatients = lastPresentIndex(firstPatients, secondPatients);
  const latest = Math.max(latestShared, latestPatients);
  const qi = quarterSel != null ? quarterSel : latest;
  const yearAgo = qi - 4;


  const at = (line, index) => (index >= 0 && line[index] != null ? line[index] : null);


  // by-serviceline bars for the selected quarter
  const barRows = (mapA, mapB) =>
    breakdown
      .map((sl) => ({
        name: sl.name,
        a: (mapA && mapA.get(sl.slug)) || 0,
        b: (mapB && mapB.get(sl.slug)) || 0,
      }))
      .filter((r) => r.a > 0 || r.b > 0)
      .sort((x, y) => y.a + y.b - (x.a + x.b))
      .slice(0, MIX_COUNT);
  const sharedBars = qi >= 0 ? barRows(firstToSecond[qi], null) : [];
  const mixBars = qi >= 0 ? barRows(firstPatients[qi], secondPatients[qi]) : [];

  // Shared patients are NEVER summed across service lines (the same patient
  // shows up in several, and a parent already includes its sub service
  // lines). With no service line picked, the trend draws one line per service
  // line and the tile reports the largest one.
  const sharedLine = (slug) =>
    firstToSecond.map((m) => (m ? m.get(slug) || 0 : null));
  const sharedSeries = selectedParent
    ? [{ name: selectedParent.name, data: sharedLine(selectedParent.slug) }]
    : parents
        .map((p) => ({ name: p.name, data: sharedLine(p.slug) }))
        .map((line) => ({
          ...line,
          total: line.data.reduce((sum, v) => sum + (v || 0), 0),
        }))
        .filter((line) => line.total > 0)
        .sort((a, b) => b.total - a.total)
        .slice(0, TREND_COUNT)
        .map(({ name, data: points }) => ({ name, data: points }));
  const hasShared = sharedSeries.some((line) => line.data.some((v) => v > 0));
  let sharedStat = selectedParent;
  if (!sharedStat && qi >= 0 && firstToSecond[qi]) {
    sharedStat = parents.reduce((top, p) => {
      const value = firstToSecond[qi].get(p.slug) || 0;
      return value > (top ? firstToSecond[qi].get(top.slug) || 0 : 0) ? p : top;
    }, null);
  }
  const sharedStatLine = sharedStat ? sharedLine(sharedStat.slug) : [];
  let sharedStatDetail = 'No shared patients in this period';
  if (sharedStat) {
    sharedStatDetail = selectedParent
      ? sharedStat.name
      : `${sharedStat.name} (largest service line)`;
  }

  // Projected patients are NEVER totalled across service lines (the same
  // patient is counted in each one). With no service line picked, the volume
  // tiles and trend follow the SAME service line as the shared-patients tile
  // so the three numbers can be read side by side; when the pair shares no
  // patients they fall back to the pair's largest combined service line.
  let volumeFocus = sharedStat;
  let volumeFocusReason = 'largest shared service line';
  if (!volumeFocus && qi >= 0) {
    const combined = (p) =>
      ((firstPatients[qi] && firstPatients[qi].get(p.slug)) || 0) +
      ((secondPatients[qi] && secondPatients[qi].get(p.slug)) || 0);
    volumeFocus = parents.reduce(
      (top, p) => (combined(p) > (top ? combined(top) : 0) ? p : top),
      null,
    );
    volumeFocusReason = 'largest combined service line';
  }
  const volumeLine = (maps) =>
    maps.map((m) => (m && volumeFocus ? m.get(volumeFocus.slug) || 0 : null));
  const firstLine = volumeLine(firstPatients);
  const secondLine = volumeLine(secondPatients);
  let volumeScopeLabel = 'No projected patients in this period';
  if (volumeFocus) {
    volumeScopeLabel = selectedParent
      ? volumeFocus.name
      : `${volumeFocus.name} (${volumeFocusReason})`;
  }
  const hasPatients =
    firstLine.some((v) => v > 0) || secondLine.some((v) => v > 0);

  const scopeLabel = selectedParent ? selectedParent.name : 'All service lines';
  const quarterLabel = qi >= 0 ? periods[qi].range : '';
  const yearAgoLabel = yearAgo >= 0 ? periods[yearAgo].range : '';

  const lineOptions = (id, colors) => ({
    chart: { id, toolbar: { show: false }, animations: { enabled: false } },
    xaxis: { categories, title: { text: '12 months ending' } },
    yaxis: {
      min: 0,
      labels: { formatter: (v) => (v == null ? '' : Math.round(v).toLocaleString()) },
    },
    stroke: { width: 3, curve: 'straight' },
    markers: { size: 4 },
    colors,
    legend: { position: 'bottom', showForSingleSeries: true },
    tooltip: {
      shared: true,
      intersect: false,
      x: { formatter: (x, opt) => periods[opt.dataPointIndex]?.range },
    },
    // The trend always spans every period; this marks the one the snapshot
    // cards are showing, so the dropdown's effect is visible here too.
    annotations: {
      xaxis:
        qi >= 0
          ? [
              {
                x: categories[qi],
                borderColor: '#8E8E8E',
                strokeDashArray: 4,
                label: {
                  text: `Selected: ${periods[qi].range}`,
                  orientation: 'horizontal',
                  // keep the label inside the plot at either end of the axis
                  textAnchor: qi > categories.length / 2 ? 'end' : 'start',
                  borderColor: '#8E8E8E',
                  style: { color: '#FFFFFF', background: '#8E8E8E', fontSize: '11px' },
                },
              },
            ]
          : [],
    },
  });
  const barOptions = (id, rows, colors) => ({
    chart: { id, toolbar: { show: false }, animations: { enabled: false } },
    xaxis: {
      categories: rows.map((r) => r.name),
      labels: { formatter: (v) => Math.round(v).toLocaleString() },
    },
    plotOptions: { bar: { horizontal: true, barHeight: '70%' } },
    dataLabels: { enabled: false },
    colors,
    legend: { position: 'bottom', showForSingleSeries: true },
    tooltip: { shared: true, intersect: false },
  });
  const pairColors = [first.color, second.color];

  // The PDF is drawn from the same values the cards render, so it can't drift
  // from the screen (direction, filters, periods, suppression stand-ins).
  const onExportPdf = () => {
    const delta = (value, previous) => {
      if (value == null || previous == null) return {};
      const diff = value - previous;
      return {
        delta: `${diff > 0 ? '+' : ''}${diff.toLocaleString()} vs. ${yearAgoLabel}`,
        deltaDirection: Math.sign(diff),
      };
    };
    const pairSeries = [
      { name: first.name, color: first.color },
      { name: second.name, color: second.color },
    ];
    const volumeTile = (who, line) => ({
      label: `Projected patients: ${who.name}`,
      detail: volumeScopeLabel,
      period: quarterLabel,
      value: at(line, qi),
      color: who.color,
      ...delta(at(line, qi), at(line, yearAgo)),
    });
    const sharedColors = selectedParent ? [first.color] : ColorUtil.COLORS;
    const charts = [
      {
        type: 'line',
        title: 'Shared Patients Over Time',
        rightLabel: selectedParent ? scopeLabel : `Top ${TREND_COUNT} service lines`,
        note: `${forwardLabel}. Each point covers the 12 months ending that month.`,
        emptyText: `No patients seen by ${forwardLabel}.`,
        categories,
        xTitle: '12 months ending',
        markIndex: qi,
        markLabel: `Selected: ${quarterLabel}`,
        series: hasShared
          ? sharedSeries.map((line, i) => ({
              ...line,
              color: sharedColors[i % sharedColors.length],
            }))
          : [],
      },
      {
        type: 'bar',
        title: 'Shared Patients by Service Line',
        rightLabel: quarterLabel,
        note: forwardLabel,
        emptyText: `No shared patients in ${quarterLabel}.`,
        series: [{ name: 'Shared patients', color: first.color }],
        rows: sharedBars.map((r) => ({ name: r.name, values: [r.a] })),
      },
    ];
    charts.push(
      {
        type: 'line',
        title: 'Projected Patients Over Time',
        rightLabel: volumeScopeLabel,
        note: 'Each provider’s own projected patients in this service line.',
        emptyText: 'No projected patients for these providers.',
        categories,
        xTitle: '12 months ending',
        markIndex: qi,
        markLabel: `Selected: ${quarterLabel}`,
        series: hasPatients
          ? [
              { ...pairSeries[0], data: firstLine },
              { ...pairSeries[1], data: secondLine },
            ]
          : [],
      },
      {
        type: 'bar',
        title: 'Service Line Mix',
        rightLabel: quarterLabel,
        note: `Projected patients, top ${MIX_COUNT} by combined volume.`,
        emptyText: `No projected patients in ${quarterLabel}.`,
        series: pairSeries,
        rows: mixBars.map((r) => ({ name: r.name, values: [r.a, r.b] })),
      },
    );
    downloadRelationshipPDF({
      title: 'Provider Relationship',
      subtitle: `${first.name} (first) and ${second.name} (second)`,
      filters: [
        `Direction: ${forwardLabel}`,
        `Service line: ${scopeLabel}`,
        `Period: ${quarterLabel}`,
      ],
      tiles: [
        {
          label: `Shared patients: ${forwardLabel}`,
          detail: sharedStatDetail,
          period: quarterLabel,
          value: at(sharedStatLine, qi),
          color: first.color,
          ...delta(at(sharedStatLine, qi), at(sharedStatLine, yearAgo)),
        },
        volumeTile(first, firstLine),
        volumeTile(second, secondLine),
      ],
      charts,
      filenameBase: `relationship_${first.provider.last_name}_${second.provider.last_name}`,
    });
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <div className={classes.pair}>
                <div className={classes.slot} style={{ borderColor: first.color }}>
                  <Typography className={classes.slotLabel}>
                    Sees the patient before
                  </Typography>
                  <Typography className={classes.slotName}>{first.name}</Typography>
                </div>
                <Tooltip title='Swap first and second'>
                  <IconButton
                    aria-label='Swap first and second provider'
                    onClick={() => setFlipped(!flipped)}
                  >
                    <Icon>swap_horiz</Icon>
                  </IconButton>
                </Tooltip>
                <div className={classes.slot} style={{ borderColor: second.color }}>
                  <Typography className={classes.slotLabel}>
                    Sees the patient after
                  </Typography>
                  <Typography className={classes.slotName}>{second.name}</Typography>
                </div>
                <div className={classes.spacer} />
                <Button color='primary' onClick={() => setFinding(true)}>
                  <Icon style={{ marginRight: 8 }}>compare_arrows</Icon>
                  Change provider
                </Button>
                <Button color='primary' disabled={isLoading} onClick={onExportPdf}>
                  <Icon style={{ marginRight: 8 }}>picture_as_pdf</Icon>
                  Export PDF
                </Button>
              </div>
              <div className={classes.filters}>
                <FormControl variant='outlined' className={classes.filter}>
                  <InputLabel>Service Line</InputLabel>
                  <Select
                    value={selectedParent ? servicelineSel : ALL}
                    onChange={(e) => setServicelineSel(e.target.value)}
                    label='Service Line'
                  >
                    <MenuItem value={ALL}>All service lines</MenuItem>
                    {parents.map((p) => (
                      <MenuItem value={p.id} key={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {qi >= 0 && (
                  <FormControl variant='outlined' className={classes.filter}>
                    <InputLabel>Period for snapshot cards</InputLabel>
                    <Select
                      value={qi}
                      onChange={(e) => setQuarterSel(e.target.value)}
                      label='Period for snapshot cards'
                    >
                      {periods.map(({ range }, index) => (
                        <MenuItem value={index} key={range}>
                          {index === latest ? `${range} (latest)` : range}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </div>
              {findDialog}
            </CardContent>
          </Card>
        </Grid>

        {isLoading && (
          <Grid item xs={12}>
            <LoadingView />
          </Grid>
        )}

        {!isLoading && (
          <>
            <Grid item xs={12} sm={4}>
              <StatCard
                classes={classes}
                label={`Shared patients: ${forwardLabel}`}
                detail={sharedStatDetail}
                period={quarterLabel}
                previousPeriod={yearAgoLabel}
                color={first.color}
                value={at(sharedStatLine, qi)}
                previous={at(sharedStatLine, yearAgo)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                classes={classes}
                label={`Projected patients: ${first.name}`}
                detail={volumeScopeLabel}
                period={quarterLabel}
                previousPeriod={yearAgoLabel}
                color={first.color}
                value={at(firstLine, qi)}
                previous={at(firstLine, yearAgo)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                classes={classes}
                label={`Projected patients: ${second.name}`}
                detail={volumeScopeLabel}
                period={quarterLabel}
                previousPeriod={yearAgoLabel}
                color={second.color}
                value={at(secondLine, qi)}
                previous={at(secondLine, yearAgo)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Card style={{ height: '100%' }}>
                <CardContent>
                  <TitleTooltip
                    title='Shared Patients Over Time'
                    tooltip='Patients the first provider saw before the second provider, per service line. Each point covers the 12 months ending in that month. Swap the providers at the top to see the other direction. Suppressed counts (under 11) are plotted as 5.'
                    rightLabel={selectedParent ? scopeLabel : `Top ${TREND_COUNT} service lines`}
                  />
                  <Typography className={classes.note}>{forwardLabel}</Typography>
                  {!hasShared && (
                    <Typography className={classes.empty}>
                      {`No patients seen by ${first.name}, then ${second.name}.`}
                    </Typography>
                  )}
                  {hasShared && (
                    <HoverLegendLineChart
                      options={lineOptions(
                        'CompareSharedTrend',
                        selectedParent ? [first.color] : ColorUtil.COLORS,
                      )}
                      series={sharedSeries}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card style={{ height: '100%' }}>
                <CardContent>
                  <TitleTooltip
                    title='Shared Patients by Service Line'
                    tooltip='Patients the first provider saw before the second provider, for the selected quarter. Swap the providers at the top to see the other direction.'
                    rightLabel={quarterLabel}
                  />
                  <Typography className={classes.note}>{forwardLabel}</Typography>
                  {sharedBars.length === 0 && (
                    <Typography className={classes.empty}>
                      {`No shared patients in ${quarterLabel}.`}
                    </Typography>
                  )}
                  {sharedBars.length > 0 && (
                    <Chart
                      type='bar'
                      height={Math.max(220, sharedBars.length * 44 + 90)}
                      options={barOptions('CompareSharedBars', sharedBars, [first.color])}
                      series={[{ name: 'Shared patients', data: sharedBars.map((r) => r.a) }]}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card style={{ height: '100%' }}>
                <CardContent>
                  <TitleTooltip
                    title='Projected Patients Over Time'
                    tooltip='Each provider’s own projected patients in one service line. With no service line picked, this follows the pair’s largest combined service line; projected patients are never totalled across service lines.'
                    rightLabel={volumeScopeLabel}
                  />
                  {!hasPatients && (
                    <Typography className={classes.empty}>
                      No projected patients for these providers.
                    </Typography>
                  )}
                  {hasPatients && (
                    <HoverLegendLineChart
                      options={lineOptions('ComparePatientsTrend', pairColors)}
                      series={[
                        { name: first.name, data: firstLine },
                        { name: second.name, data: secondLine },
                      ]}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card style={{ height: '100%' }}>
                <CardContent>
                  <TitleTooltip
                    title='Service Line Mix'
                    tooltip={`Projected patients by service line for the selected quarter, top ${MIX_COUNT} by combined volume.`}
                    rightLabel={quarterLabel}
                  />
                  {mixBars.length === 0 && (
                    <Typography className={classes.empty}>
                      {`No projected patients in ${quarterLabel}.`}
                    </Typography>
                  )}
                  {mixBars.length > 0 && (
                    <Chart
                      type='bar'
                      height={Math.max(220, mixBars.length * 44 + 90)}
                      options={barOptions('CompareMixBars', mixBars, pairColors)}
                      series={[
                        { name: first.name, data: mixBars.map((r) => r.a) },
                        { name: second.name, data: mixBars.map((r) => r.b) },
                      ]}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
}

ProviderCompare.propTypes = {
  classes: PropTypes.object.isRequired,
  provider: PropTypes.object,
};

const Styled = withStyles(styles)(ProviderCompare);
export { Styled as ProviderCompare };
